// Storm fronts (roadmap #12's look, TODO "moving weather fronts"): on storm
// rounds a line of anvil-topped thunderheads marches across the map, lightning
// flickering in their bellies, bolts cracking down to the ground, rain
// curtains dragging beneath. Theater-agnostic by construction — the only
// gameplay input is the weather byte every theater's snapshot already
// carries, so air, naval, land, and any future mode get the same sky.
//
// Determinism is the whale-schedule trick from ambient.js: every position,
// flicker, and strike is a pure function of (world seed, server time), so all
// clients watch the same storm with zero wire traffic. The front's path wraps
// forever — crossing k of the map ends where crossing k+1 begins with a fresh
// hashed bearing — which means a client joining mid-round lands on exactly
// the same sky as everyone else, no round-start state needed.
//
// Purely audiovisual: nothing here pushes on planes, hulls, spotting, or
// scores. A client with storms hidden runs an identical simulation.

import * as THREE from "three";
import { hash2 } from "./terrain_core.js";
import { makeGlowTexture } from "./world.js";
import { cardMesh, cardUniforms, puffMid, PUFF_GRID } from "./clouds.js";
import { uploadInstances } from "./instance_upload.js";

const LIGHTNING_CORE = 0xeaf2ff; // the bolt itself: cold blue-white
const GLOW_TINT = 0xcfe0ff; // in-cloud flicker and ground flash

// The front's puffs, per cell (skirt, tower, anvil) and along the squall
// line: 475, a quarter of the old box count. A card is a whole rounded puff
// where a box was one sliver of one, and every card is fill a phone pays
// for — at 718 the front cost a software-rendered frame up to a fifth more
// than the boxes did inside the cloud band; at this count it costs no more
// than they did from ahead, afar, under the base or inside it.
const SKIRT = 24, TOWER = 36, ANVIL = 20;
const PER_CELL = SKIRT + TOWER + ANVIL;
const LINE_LOW = 100, LINE_ANVIL = 55;
// Denser than the fair-weather cumulus (0.95 there): a thunderhead is the
// one cloud you can't see the sky through.
const STORM_OPACITY = 1;
// clouds.js's shade gains (ambient, sun, rim, fog thinning) for the front:
// the cumulus's light, no silver rim (it traced every seam between the dark
// puffs in white), and less thinning into the haze — a squall line on the
// horizon is a wall, not a watercolour. The ambient gain sits under the
// cumulus's: a thunderhead is lit by the sky it blots out, not the open one.
const STORM_SHADE = [0.45, 0.27, 0, 0.3];
// A far thunderhead greys into the haze as any cloud does, but toward a
// darker grey than the haze itself (the fog colour times 1 minus this), so
// the squall line on the horizon stays a bruise, not a pale watercolour wall.
const STORM_HAZE_DARK = 0.5;
// How far a lightning stroke's light reaches through its cell, in cell radii
// (the falloff of the per-card glow): the heart of the cell flares, its
// flanks catch the edge of it, and the rest of the thunderhead stays dark.
const GLOW_REACH = 0.5;
// How hard a stroke lights the puffs round it (per-card glow, capped at 1):
// enough to saturate the heart of the cell, not to bleach the whole tower
// white for the length of every flicker.
const GLOW_GAIN = 1.5;
// Near-fade span in frame sizes (the cumulus use 0.34..0.75): a puff here is
// half a kilometre across, so it thins only once you're truly in it — and
// by then the storm's own fog has closed in.
const STORM_NEAR_FADE = [0.18, 0.42];
// Inside the cloud band the front is the dearest thing in the sky: every puff
// round the eye is several hundred metres across, and a pilot in the thick of
// the line looks through a dozen screens of them stacked back to front. The
// near ones are the whiteout you fly in; the ones past three quarters of a
// cell's radius sit behind them and in the storm's own fog, so there the
// front sheds up to this share of its puffs as insideK climbs. Priced on the
// RTX (the front alone, GPU timer, 2560x1440): 12-14 % off in the thick of the
// squall line and 20-31 % in the heart of a cell, moving under 4 % of the
// frame's pixels; shedding the near puffs as well moved half of them.
const THIN_MAX = 0.5;
// insideK over which the shedding eases in: not under the base (0.3 at most
// there, where you look up at the whole belly), and in full once you're deep.
const THIN_K = [0.25, 0.9];
// A puff sheds over this span of rank, so shedding sweeps through the deck
// as insideK climbs instead of dropping cards whole; and a puff thins over
// the last quarter of a cell radius out to the shedding line, so none pops as
// the eye moves.
const THIN_RAMP = 0.08;
// Near the front the puffs draw at half the frame's size (drawPuffs), in a
// pass of their own, and one screen-sized card lays them in at the front's
// place. The puffs are soft, so a quarter of the pixels is a quarter of the
// fill and nobody can tell (the frame differs by under 1/255 on average);
// but the pass and the lay cost a fixed couple of tenths of a millisecond,
// about what the whole front costs from afar. Priced on the RTX (the front
// alone, GPU timer, 2560x1440): 30-40 % off ahead of the front, under its
// belly and in the thick of it, nothing gained at three and a half cell
// radii. So it waits for the nearest puff to come within the first of these
// (in cell radii), and holds until it's past the second, so the switch
// doesn't flicker on the way in.
const HALF_NEAR = [1.5, 1.8];

export class StormFront {
  constructor(scene, field, config) {
    const cfg = config.storm ?? {};
    this.scene = scene;
    this.field = field;
    // Bigger maps (naval fights at 3x the air map's radius) get bigger, faster
    // cells so a front still reads as weather, not a lost puff — and crosses
    // in about the same wall-clock time.
    const mapK = Math.max(1, Math.min(2.6, field.radius / 3600));
    this.cellN = cfg.cellCount ?? 4;
    this.cellR = (cfg.cellRadiusM ?? 850) * mapK;
    this.baseY = cfg.cloudBaseM ?? 380;
    this.topY = cfg.cloudTopM ?? 2100;
    this.speed = (cfg.marchSpeedMS ?? 12) * mapK;
    this.flickerEvery = cfg.flickerEverySec ?? 5;
    this.strikeEvery = cfg.strikeEverySec ?? 16;
    this.seed = (field.seed + 62199) >>> 0;
    // Path length: the front walks fully onto the map and fully off the far
    // side before the next crossing re-rolls its bearing.
    this.pathLen = 2 * (field.radius + this.cellR * 2.2);

    this.active = false;
    this.built = false;
    // No crossing dealt yet. Not -1: that IS a crossing (a clock before the
    // world's zero, which the screenshot director's pinned hours reach), and
    // a front that thought it had already dealt it drew no cells at all.
    this.crossK = null;
    this.cellOff = []; // per-cell local [x,z] within the front group
    this.cellWorld = []; // per-cell world-space centers, refreshed per frame
    this.strikes = []; // per-cell live strike state (world-anchored)
    this.onStrike = null; // (Vector3 world pos) => {} — main.js hangs thunder here
    this.insideK = 0; // 0..1 "camera is in the weather" (debug/test introspection)
    // Draw the puffs at half size near the front (HALF_NEAR) when main.js
    // runs drawPuffs before the frame; it turns this off without WebGL2.
    this.halfRes = true;
    this.halfOn = false; // near enough that drawPuffs takes them (HALF_NEAR)
  }

  // Lazy build: four rounds in five never storm, so the ~1500 instances only
  // exist once a storm round actually arrives.
  build() {
    this.built = true;
    const N = this.cellN;
    const r = this.cellR;
    const seed = this.seed;
    this.group = new THREE.Group();
    this.group.visible = false;
    this.scene.add(this.group);

    // --- the cloud mass: one card draw for the whole front -----------------
    // Puffs cut from the same Blender-baked atlas as the fair-weather cumulus
    // (clouds.js), lit by the same sun, so the storm and the sky it swallows
    // speak one language. The front's shape is the one the box recipe found
    // the hard way: per-cell convective towers (the lightning anchors) and a
    // continuous squall line — a ragged dark base wall plus one connected
    // anvil sheet spanning the whole front — so the cells merge into a single
    // storm system instead of four lonely castles. The boxes' lesson carries
    // over too: many heavily-overlapped middling puffs read as weather, where
    // a few giant ones read as a painted backdrop (and paper-thin sheets, as
    // ever, read as smog). Each card rides the front's group, so the whole
    // mass marches with one matrix.
    this.spacing = r * 1.7; // cells overlap through the line mass
    const halfLine = ((N - 1) / 2) * this.spacing + r * 0.7;
    this.halfLine = halfLine;
    const anvilY = this.baseY + 0.62 * (this.topY - this.baseY);
    const col = new THREE.Color();
    // A thunderhead is a dark thing even in daylight: the belly all but
    // black, the tower slate, and the sun only greys the anvil it catches.
    const cLow = new THREE.Color(0.09, 0.105, 0.135); // rain-black belly
    const cHigh = new THREE.Color(0.27, 0.3, 0.35); // slate mid-tower
    const cAnvil = new THREE.Color(0.4, 0.43, 0.49); // sun catches the top
    const shade = (y, b, cs) => {
      const hFrac = (y - this.baseY) / (this.topY - this.baseY);
      col.copy(cLow).lerp(cHigh, Math.min(1, Math.max(0, hFrac) * 1.05));
      if (y > anvilY) col.lerp(cAnvil, (y - anvilY) / (this.topY - anvilY));
      const jit = 0.94 + 0.12 * hash2(b, 6, cs);
      return [col.r * jit, col.g * jit, col.b * jit];
    };
    // One puff: centred on (x, yMid, z) in front-local metres, `wide` across
    // its body, `squash` its height scale (anvil plates run flat). The atlas
    // puff fills about 62 % of its frame's width, and a card's middle sits
    // 0.3 frames above its base (clouds.js puffMid) — hence the two constants.
    const puff = (x, yMid, z, wide, squash, b, cs) => {
      const size = wide / 0.62;
      const [cr, cg, cb] = shade(yMid, b, cs);
      return {
        x, y: yMid - size * squash * 0.3, z, size, squash,
        cell: Math.floor(hash2(b, 7, cs) * PUFF_GRID * PUFF_GRID),
        mirror: hash2(b, 8, cs) < 0.5 ? -1 : 1,
        opacity: 0.85 + 0.15 * hash2(b, 9, cs),
        r: cr, g: cg, b: cb,
      };
    };
    this.towerLocal = []; // per-cell puff layout, cell-relative (built once)
    for (let i = 0; i < N; i++) {
      const puffs = [];
      const cs = (seed + i * 131) >>> 0;
      for (let b = 0; b < PER_CELL; b++) {
        const h1 = hash2(b, 1, cs), h2 = hash2(b, 2, cs), h3 = hash2(b, 3, cs);
        const h4 = hash2(b, 4, cs), h5 = hash2(b, 5, cs);
        if (b < SKIRT) {
          // Skirt: wide, low, the darkest puffs — the wall you fly under.
          const ang = h1 * Math.PI * 2, rad = r * (0.15 + 0.85 * Math.sqrt(h2));
          puffs.push(puff(Math.cos(ang) * rad, this.baseY + 40 + h3 * 150, Math.sin(ang) * rad,
            r * (0.3 + 0.22 * h4), 0.55, b, cs));
        } else if (b < SKIRT + TOWER) {
          // Tower: the convective column, narrowing as it climbs.
          const t = h3;
          const ang = h1 * Math.PI * 2;
          const rad = r * (0.8 - 0.5 * t) * Math.sqrt(h2);
          puffs.push(puff(Math.cos(ang) * rad, this.baseY + 150 + t * (anvilY - this.baseY - 150), Math.sin(ang) * rad,
            r * (0.3 + 0.22 * h4) * (1 - 0.3 * t), 0.8 + 0.3 * h5, b, cs));
        } else {
          // Anvil: flat plates spreading at the cap and streaming forward
          // (+Z is the march direction — the shear the shape is named for).
          const t = h3; // 0 anvil base -> 1 cap
          const spread = r * (0.8 + 1.1 * t);
          const ang = h1 * Math.PI * 2, rad = spread * Math.sqrt(h2);
          puffs.push(puff(Math.cos(ang) * rad, anvilY + t * (this.topY - anvilY),
            Math.sin(ang) * rad + t * r * 0.7 * h5, r * (0.45 + 0.35 * h4), 0.32, b, cs));
        }
      }
      this.towerLocal.push(puffs);
    }
    // The squall line: group-local, crossing-independent (only the cells
    // re-jitter per crossing), so it bakes once here and re-lays verbatim.
    this.lineLocal = [];
    {
      const cs = (seed + 7331) >>> 0;
      for (let b = 0; b < LINE_LOW; b++) {
        const h1 = hash2(b, 11, cs), h2 = hash2(b, 12, cs), h3 = hash2(b, 13, cs);
        const h4 = hash2(b, 14, cs);
        const yMid = this.baseY + 40 + Math.pow(h3, 1.6) * 440; // bias into the dark base
        this.lineLocal.push(puff((h1 - 0.5) * 2 * halfLine, yMid, (h2 - 0.5) * r * 1.5,
          r * (0.28 + 0.24 * h4), 0.6, b, cs));
      }
      for (let b = 0; b < LINE_ANVIL; b++) {
        const h1 = hash2(b, 21, cs), h2 = hash2(b, 22, cs), h3 = hash2(b, 23, cs);
        const h4 = hash2(b, 24, cs), h5 = hash2(b, 25, cs);
        const t = h3;
        this.lineLocal.push(puff((h1 - 0.5) * 2 * (halfLine + r * 0.3), anvilY + 80 + t * (this.topY - anvilY - 80),
          (h2 - 0.5) * r * 1.8 + t * r * 0.6 * h5, r * (0.5 + 0.4 * h4), 0.28, 100 + b, cs));
      }
    }
    // The deck the cards are drawn from: the towers' puffs are copied in per
    // crossing (layoutCells), the line's ride as dealt.
    this.cards = [];
    for (let i = 0; i < N; i++) for (const p of this.towerLocal[i]) this.cards.push({ ...p });
    for (const p of this.lineLocal) this.cards.push(p);
    // The fair-weather decks' light and atlas when main.js has handed them
    // over (it always has by a storm round: the build is lazy); a front built
    // without them (tests) draws dark until an atlas lands.
    // The front keeps two gains of its own (STORM_SHADE, STORM_NEAR_FADE):
    // no silver rim, and a near-fade that doesn't empty the ceiling over
    // anyone flying under the base.
    const shared = this.cloudDecks?.uniforms ?? cardUniforms();
    this.sharedAtlas = shared.uAtlas;
    this.cardUniforms = {
      ...shared,
      uAtlas: { value: null }, // the same picture, filtered for the front (sortCards)
      uShade: { value: new THREE.Vector4(...STORM_SHADE) },
      uNearFade: { value: new THREE.Vector2(...STORM_NEAR_FADE) },
    };
    this.towers = cardMesh(this.cards, { opacity: STORM_OPACITY, uniforms: this.cardUniforms, glow: true });
    this.towers.material.uniforms.uHazeDark.value = STORM_HAZE_DARK;
    this.group.add(this.towers);
    this.low = lowResPuffs(this.towers);
    this.group.add(this.low.comp);

    // --- rain shafts: a faint gray veil from cloud base to the ground,
    // under the cells AND the line between them — one curtain, not four.
    // Each curtain is a box, but one that fades out toward its side edges and
    // thins toward the ground (rainAlphaMap): a hard-sided slab under soft
    // cloud read as a glass wall, a feathered one reads as falling rain.
    this.veil = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshLambertMaterial({
        color: 0x7d8894, transparent: true, opacity: 0.16, depthWrite: false,
        alphaMap: rainAlphaMap(),
      }),
      N * 7 + (N - 1) * 4,
    );
    this.veil.frustumCulled = false;
    this.group.add(this.veil);

    // --- rain streaks: falling instanced slivers under each cell -----------
    this.RAIN_PER_CELL = 120;
    this.rain = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshLambertMaterial({
        color: 0xa9c0d4, transparent: true, opacity: 0.4, depthWrite: false,
      }),
      N * this.RAIN_PER_CELL,
    );
    this.rain.frustumCulled = false;
    this.group.add(this.rain);

    // --- close rain: a small camera-following curtain so being under the
    // cell feels like rain on the canopy, not just scenery in the distance.
    this.CLOSE_RAIN = 200;
    this.closeRain = new THREE.InstancedMesh(
      new THREE.BoxGeometry(0.14, 9, 0.14),
      new THREE.MeshBasicMaterial({
        color: 0xbdd2e2, transparent: true, opacity: 0, depthWrite: false,
      }),
      this.CLOSE_RAIN,
    );
    this.closeRain.frustumCulled = false;
    this.scene.add(this.closeRain); // camera-space, not part of the front

    // --- lightning: an in-cloud glow sprite per cell, a ground flash, a
    // shared pool of bolt segments — and the glow that actually matters:
    // per-instance CLOUD COLORS lerp toward the lightning tint around a
    // live stroke (see update), so the cloud itself glows. A real
    // PointLight can't do this job here: three r160's physical
    // inverse-square falloff is stone dead across cloud-sized distances.
    const glowTex = makeGlowTexture();
    this.glows = [];
    this.groundGlows = [];
    for (let i = 0; i < N; i++) {
      const g = new THREE.Sprite(new THREE.SpriteMaterial({
        map: glowTex, color: GLOW_TINT, transparent: true, opacity: 0,
        depthWrite: false, blending: THREE.AdditiveBlending,
      }));
      g.scale.set(this.cellR * 1.7, this.cellR * 1.2, 1);
      this.group.add(g);
      this.glows.push(g);
      const gg = new THREE.Sprite(new THREE.SpriteMaterial({
        map: glowTex, color: GLOW_TINT, transparent: true, opacity: 0,
        depthWrite: false, blending: THREE.AdditiveBlending,
      }));
      gg.scale.set(320, 220, 1);
      this.scene.add(gg); // world-anchored with its bolt
      this.groundGlows.push(gg);
    }
    // Per-card illumination bookkeeping: owning cell and distance falloff
    // to that cell's glow point (filled by layoutCells).
    this.instCell = new Uint8Array(this.cards.length);
    this.instK = new Float32Array(this.cards.length);
    this._wasLit = false;
    // Segment budget: a stair-stepped ground bolt runs ~25 boxes plus a
    // fork; a cloud-to-cloud arc ~17. The mesh draws only the segments a
    // live stroke laid this frame (bolt.count), so the sky between flashes
    // sends the card nothing — no pool of parked zero-scale boxes re-uploaded
    // sixty times a second for a strike that lands every half minute.
    this.C2G_SEGS = 34;
    this.C2C_SEGS = 18;
    this.BOLT_CAP = N * this.C2G_SEGS + (N - 1) * this.C2C_SEGS;
    this.bolt = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({
        color: LIGHTNING_CORE, transparent: true, opacity: 0,
        depthWrite: false, blending: THREE.AdditiveBlending, fog: false,
      }),
      this.BOLT_CAP,
    );
    this.bolt.count = 0;
    this.bolt.frustumCulled = false;
    this.scene.add(this.bolt);

    this.dummy = new THREE.Object3D();
    this.strikes = new Array(N).fill(null);
    this.lastStrikeSlot = new Array(N).fill(-1);
    this.arcs = new Array(Math.max(0, N - 1)).fill(null);
    this.lastArcSlot = new Array(Math.max(0, N - 1)).fill(-1);
    this.cellGlow = new Array(N).fill(0);
  }

  // Re-deal the front line for crossing k: cells sit abreast of the march
  // (local X), staggered and jittered so no two crossings look alike.
  layoutCells(k) {
    this.crossK = k;
    const N = this.cellN;
    this.cellOff.length = 0;
    for (let i = 0; i < N; i++) {
      const ox = (i - (N - 1) / 2) * this.spacing + (hash2(k, 21 + i, this.seed) - 0.5) * this.cellR * 0.7;
      const oz = (hash2(k, 41 + i, this.seed) - 0.5) * this.cellR * 1.6;
      this.cellOff.push([ox, oz]);
    }
    // Glow anchors first (the flicker/strike hearts) — every block's
    // illumination falloff is measured against its cell's anchor.
    const gp = [];
    for (let i = 0; i < N; i++) {
      const [ox, oz] = this.cellOff[i];
      gp.push([
        ox + (hash2(k, 81 + i, this.seed) - 0.5) * this.cellR * 0.6,
        this.baseY + (this.topY - this.baseY) * 0.32,
        oz + (hash2(k, 91 + i, this.seed) - 0.5) * this.cellR * 0.6,
      ]);
    }
    // Deal the cloud mass for this crossing: each cell's puffs move with
    // their cell, the squall line stays put. Alongside, remember each card's
    // owning cell and glow falloff so lightning can relight the mass per
    // frame without recomputing any of this.
    const mid = [0, 0, 0];
    let n = 0;
    const own = (c, ci) => {
      puffMid(c, mid);
      if (ci === undefined) {
        // Line puffs belong to whichever cell's glow sits closest.
        let bd = Infinity;
        ci = 0;
        for (let i = 0; i < N; i++) {
          const d = Math.hypot(mid[0] - gp[i][0], mid[1] - gp[i][1], mid[2] - gp[i][2]);
          if (d < bd) { bd = d; ci = i; }
        }
      }
      const d = Math.hypot(mid[0] - gp[ci][0], mid[1] - gp[ci][1], mid[2] - gp[ci][2]);
      this.instCell[n] = ci;
      this.instK[n] = Math.exp(-d / (this.cellR * GLOW_REACH));
      n++;
    };
    for (let i = 0; i < N; i++) {
      const [ox, oz] = this.cellOff[i];
      for (const p of this.towerLocal[i]) {
        const c = this.cards[n];
        c.x = ox + p.x;
        c.z = oz + p.z;
        own(c, i);
      }
    }
    for (const c of this.lineLocal) own(c, undefined);
    this.towers.userData.sort(this._eyeLocal ?? { x: 0, y: 0, z: 0 }, true);
    let v = 0;
    for (let i = 0; i < N; i++) {
      const [ox, oz] = this.cellOff[i];
      const cs = (this.seed + k * 977 + i * 131) >>> 0;
      for (let b = 0; b < 7; b++) {
        const ang = hash2(b, 61, cs) * Math.PI * 2;
        const rad = this.cellR * 0.55 * Math.sqrt(hash2(b, 62, cs));
        const w = this.cellR * (0.35 + 0.3 * hash2(b, 63, cs));
        this.dummy.position.set(ox + Math.cos(ang) * rad, this.baseY / 2, oz + Math.sin(ang) * rad);
        this.dummy.scale.set(w, this.baseY, w * 0.8);
        this.dummy.rotation.set(0, ang, 0);
        this.dummy.updateMatrix();
        this.veil.setMatrixAt(v++, this.dummy.matrix);
      }
      // ...and a rain curtain in the gap toward the next cell over.
      if (i + 1 < N) {
        const [nx, nz] = [
          (i + 1 - (N - 1) / 2) * this.spacing, 0,
        ];
        for (let b = 0; b < 4; b++) {
          const fx = ox + (nx - ox) * (0.2 + 0.6 * hash2(b, 64, cs));
          const fz = oz * (1 - b / 4) + (hash2(b, 65, cs) - 0.5) * this.cellR * 0.7;
          const w = this.cellR * (0.3 + 0.25 * hash2(b, 66, cs));
          this.dummy.position.set(fx, this.baseY / 2, fz);
          this.dummy.scale.set(w, this.baseY, w * 0.8);
          this.dummy.rotation.set(0, hash2(b, 67, cs) * Math.PI, 0);
          this.dummy.updateMatrix();
          this.veil.setMatrixAt(v++, this.dummy.matrix);
        }
      }
      // In-cloud glow hangs at the cell's anchor (same point the block
      // illumination falls off from).
      this.glows[i].position.set(gp[i][0], gp[i][1], gp[i][2]);
    }
    this.veil.count = v;
    this.veil.instanceMatrix.needsUpdate = true;
  }

  set(name) {
    const want = name === "storm";
    if (want && !this.built) this.build();
    this.active = want;
    // The fair-weather decks (clouds.js's cumulus) dim under a storm —
    // bright sunlit cumulus over a black anvil line reads as two
    // skies arguing. main.js hands us the decks; restored when the round
    // clears. (Idempotent: set() runs on every snapshot.)
    if (this.cloudDecks) {
      const { low, high } = this.cloudDecks;
      if (this._deckOp === undefined) this._deckOp = [low.material.opacity, high.material.opacity];
      low.material.opacity = want ? this._deckOp[0] * 0.5 : this._deckOp[0];
      high.material.opacity = want ? this._deckOp[1] * 0.45 : this._deckOp[1];
    }
    if (this.built) {
      this.group.visible = want;
      if (!want) {
        this.swallowDecks(null);
        this.closeRain.material.opacity = 0;
        this.bolt.material.opacity = 0;
        this.bolt.count = 0;
        for (const g of this.groundGlows) g.material.opacity = 0;
        this.strikes.fill(null);
        this.arcs.fill(null);
      }
    }
  }

  // --- the deterministic lightning schedule, factored so update(), tests,
  // and the screenshot director all read the same dice ---------------------
  strikeRolls(slot, i) {
    return hash2(slot, 400 + i, this.seed) < 0.5;
  }
  strikeFireT(slot, i) {
    return slot * this.strikeEvery + hash2(slot, 410 + i, this.seed) * (this.strikeEvery - 1);
  }
  strikeBright(slot, i, u) {
    const ph = hash2(slot, 430 + i, this.seed);
    return Math.exp(-2.5 * u) * (0.45 + 0.55 * Math.abs(Math.sin(u * (36 + 30 * ph))));
  }
  // nextStrikeAt scans ahead from server-time fromT and returns the next
  // ground strike at its brightest instant: {t, cell, bright}. The director
  // parks cameras with this; nobody has to sit in the rain waiting.
  nextStrikeAt(fromT, horizonSec = 1800) {
    for (let slot = Math.floor(fromT / this.strikeEvery); slot * this.strikeEvery < fromT + horizonSec; slot++) {
      for (let i = 0; i < this.cellN; i++) {
        if (!this.strikeRolls(slot, i)) continue;
        const tFire = this.strikeFireT(slot, i);
        if (tFire < fromT) continue;
        let bt = tFire, bb = 0;
        for (let u = 0; u < 1; u += 0.02) {
          const b = this.strikeBright(slot, i, u);
          if (b > bb) { bb = b; bt = tFire + u * 0.65; }
        }
        return { t: bt, cell: i, bright: bb };
      }
    }
    return null;
  }

  // The front's pose at server-time t: crossing index, world position of the
  // line's center, and the march bearing (front-local +Z in world space).
  poseAt(t) {
    const s = t * this.speed;
    const k = Math.floor(s / this.pathLen);
    const along = s - k * this.pathLen - this.pathLen / 2;
    const ang = hash2(k, 7, this.seed) * Math.PI * 2;
    const dx = Math.sin(ang), dz = Math.cos(ang);
    // Each crossing also slides the whole track sideways so the front
    // doesn't always march through the map's center.
    const lat = (hash2(k, 8, this.seed) - 0.5) * this.field.radius * 0.9;
    return { k, x: dx * along - dz * lat, z: dz * along + dx * lat, ang };
  }

  update(dt, svrT, camera, sky) {
    if (!this.active || !this.built) {
      if (sky) sky.lightning = 0;
      this.insideK = 0;
      return;
    }
    const N = this.cellN;
    const pose = this.poseAt(svrT);
    if (pose.k !== this.crossK) this.layoutCells(pose.k);
    this.group.position.set(pose.x, 0, pose.z);
    this.group.rotation.y = pose.ang;
    this.sortCards(camera);
    this.swallowDecks(pose);
    const sinA = Math.sin(pose.ang), cosA = Math.cos(pose.ang);

    // World-space cell centers (the group only yaws + translates, so this is
    // a 2D rotation — no matrix math needed).
    this.cellWorld.length = 0;
    for (let i = 0; i < N; i++) {
      const [ox, oz] = this.cellOff[i];
      this.cellWorld.push([pose.x + ox * cosA + oz * sinA, pose.z - ox * sinA + oz * cosA]);
    }

    // --- rain streaks: recycle down the shaft on the shared clock ----------
    const FALL = 62;
    const span = this.baseY + 60;
    let n = 0;
    for (let i = 0; i < N; i++) {
      const [ox, oz] = this.cellOff[i];
      const cs = (this.seed + i * 517) >>> 0;
      for (let b = 0; b < this.RAIN_PER_CELL; b++) {
        const ang = hash2(b, 71, cs) * Math.PI * 2;
        const rad = this.cellR * 0.8 * Math.sqrt(hash2(b, 72, cs));
        const phase = hash2(b, 73, cs) * span;
        const y = this.baseY + 20 - ((svrT * FALL + phase) % span);
        this.dummy.position.set(ox + Math.cos(ang) * rad, y, oz + Math.sin(ang) * rad);
        this.dummy.scale.set(2.2, 26 + hash2(b, 74, cs) * 18, 2.2);
        this.dummy.rotation.set(0, 0, 0);
        this.dummy.updateMatrix();
        this.rain.setMatrixAt(n++, this.dummy.matrix);
      }
    }
    this.rain.instanceMatrix.needsUpdate = true;

    // --- how deep in the weather is the camera? ----------------------------
    // Horizontal closeness to the nearest cell, scaled by altitude: inside
    // the cloud band you get the full whiteout; below the base you're only
    // in the rain (a softer squeeze — hull crews on the sea/steppe keep
    // enough visibility to fight, and server spotting is untouched).
    let inK = 0;
    let nearCell = 0;
    for (let i = 0; i < N; i++) {
      const [wx, wz] = this.cellWorld[i];
      const d = Math.hypot(camera.position.x - wx, camera.position.z - wz);
      const k = Math.max(0, 1 - d / this.cellR);
      if (k > inK) { inK = k; nearCell = i; }
    }
    const camY = camera.position.y;
    const altK = camY > this.baseY * 0.85
      ? (camY < this.topY + 200 ? 1 : Math.max(0, 1 - (camY - this.topY - 200) / 400))
      : 0.3;
    const insideK = Math.min(1, inK * 2.2) * altK;
    this.insideK = insideK;
    this.thinCards(insideK);
    if (insideK > 0.01) {
      // Deepen the fog the sky just set — knife-fight range inside a cell.
      this.scene.fog.near *= 1 - 0.72 * insideK;
      this.scene.fog.far *= 1 - 0.5 * insideK;
    }

    // --- close rain follows the camera while under/inside the weather ------
    const wet = inK > 0.05 && camY < this.topY ? Math.min(1, inK * 2.5) : 0;
    if (wet > 0.01) {
      const cs = (this.seed + 909) >>> 0;
      for (let b = 0; b < this.CLOSE_RAIN; b++) {
        const bx = (hash2(b, 81, cs) - 0.5) * 70;
        const bz = (hash2(b, 82, cs) - 0.5) * 70;
        const phase = hash2(b, 83, cs) * 60;
        const y = 30 - ((svrT * FALL + phase) % 60);
        this.dummy.position.set(camera.position.x + bx, camera.position.y + y, camera.position.z + bz);
        this.dummy.scale.set(1, 1, 1);
        this.dummy.rotation.set(0, 0, 0);
        this.dummy.updateMatrix();
        this.closeRain.setMatrixAt(b, this.dummy.matrix);
      }
      this.closeRain.instanceMatrix.needsUpdate = true;
    }
    this.closeRain.material.opacity = wet * 0.55;

    // --- lightning ----------------------------------------------------------
    // Everything below is scheduled from hashed time slots, so every client
    // sees the same flicker at the same instant — a shared "did you see
    // that?!" moment, like the whales.
    let flash = 0; // feeds sky.lightning: the scene-wide cold pulse
    const cg = this.cellGlow;
    cg.fill(0);
    for (let i = 0; i < N; i++) {
      // In-cloud flicker: most slots glow somewhere in the belly — the
      // storm muttering to itself between the real strokes.
      const fSlot = Math.floor(svrT / this.flickerEvery);
      if (hash2(fSlot, 300 + i, this.seed) < 0.65) {
        const tFire = fSlot * this.flickerEvery + hash2(fSlot, 310 + i, this.seed) * (this.flickerEvery - 0.6);
        const u = (svrT - tFire) / 0.55;
        if (u >= 0 && u < 1) {
          const ph = hash2(fSlot, 320 + i, this.seed);
          cg[i] = Math.exp(-3 * u) * (0.35 + 0.65 * Math.abs(Math.sin(u * (22 + 14 * ph) + ph * 7)));
        }
      }

      // Ground strikes: rarer, harder, and they touch the actual terrain.
      let striking = false;
      const sSlot = Math.floor(svrT / this.strikeEvery);
      if (this.strikeRolls(sSlot, i)) {
        const tFire = this.strikeFireT(sSlot, i);
        // 0.65 s of life: a first hard stroke, then the restrikes real
        // lightning does — long enough for a human (or a screenshot) to
        // actually catch it.
        const u = (svrT - tFire) / 0.65;
        if (u >= 0 && u < 1) {
          striking = true;
          if (this.lastStrikeSlot[i] !== sSlot) {
            this.lastStrikeSlot[i] = sSlot;
            this.buildStrike(i, sSlot);
            if (this.onStrike && this.strikes[i]) this.onStrike(this.strikes[i].hit);
          }
          const st = this.strikes[i];
          if (st) {
            const b = this.strikeBright(sSlot, i, u);
            st.bright = b;
            this.groundGlows[i].position.copy(st.hit).add(_up12);
            this.groundGlows[i].material.opacity = b * 0.85;
            cg[i] = Math.max(cg[i], b);
            flash = Math.max(flash, b * 0.8);
          }
        }
      }
      if (!striking && this.strikes[i]) {
        this.strikes[i] = null;
        this.groundGlows[i].material.opacity = 0;
      }
    }

    // Cloud-to-cloud strokes: a stair-stepped arc crawling the bellies
    // between neighbor cells. It never touches the ground — its whole job
    // is lighting two cells from within at once.
    const c2cEvery = this.flickerEvery + 2.5;
    for (let j = 0; j + 1 < N; j++) {
      let arcing = false;
      const aSlot = Math.floor(svrT / c2cEvery);
      if (hash2(aSlot, 470 + j, this.seed) < 0.5) {
        const tFire = aSlot * c2cEvery + hash2(aSlot, 480 + j, this.seed) * (c2cEvery - 0.6);
        const u = (svrT - tFire) / 0.4;
        if (u >= 0 && u < 1) {
          arcing = true;
          if (this.lastArcSlot[j] !== aSlot) {
            this.lastArcSlot[j] = aSlot;
            this.buildArc(j, aSlot);
            if (this.onArc && this.arcs[j]) this.onArc(this.arcs[j].mid);
          }
          const arc = this.arcs[j];
          if (arc) {
            const ph = hash2(aSlot, 490 + j, this.seed);
            const b = Math.exp(-3 * u) * (0.4 + 0.6 * Math.abs(Math.sin(u * (28 + 22 * ph))));
            arc.bright = b;
            cg[j] = Math.max(cg[j], b * 0.9);
            cg[j + 1] = Math.max(cg[j + 1], b * 0.9);
            flash = Math.max(flash, b * 0.45);
          }
        }
      }
      if (!arcing && this.arcs[j]) this.arcs[j] = null;
    }

    // THE CLOUDS GLOW: puffs near a live stroke flare toward a hot
    // blue-white, scaled by their baked falloff from the cell's glow anchor.
    // This is a per-card level, not a light — cheap, and it reads from any
    // distance. The nearest puffs saturate into a real flare.
    let lit = false;
    for (let i = 0; i < N; i++) if (cg[i] > 0.02) lit = true;
    if (lit || this._wasLit) {
      const glow = this.towers.userData.glow;
      for (let m = 0; m < glow.length; m++) {
        glow[m] = Math.min(1, cg[this.instCell[m]] * this.instK[m] * GLOW_GAIN);
      }
      this.towers.userData.pushGlow();
    }
    this._wasLit = lit;
    for (let i = 0; i < N; i++) {
      const g = Math.min(1, cg[i]);
      this.glows[i].material.opacity = g * 0.9;
      flash = Math.max(flash, g * 0.3);
    }
    if (sky) sky.lightning = flash;
    this.drawStrikes();
  }

  // The puffs draw back to front from the eye, taken into the marching
  // group's frame (the cards' own). Among the other see-through draws the
  // front sorts at its nearest puff, straight ahead — as the fair-weather
  // decks do — so whichever cloud is nearer lays over the other.
  sortCards(camera) {
    const t = this.towers;
    // The cumulus's atlas once it lands, as a second view of the same image
    // with plain trilinear filtering: the front is the most overdrawn thing
    // in the sky, and anisotropic taps made every software-rendered layer of
    // it dearer (a fifth of the front's cost from ahead) for a sharpness
    // nobody sees in a thunderhead. The cumulus keep theirs.
    const atlas = this.sharedAtlas.value;
    if (atlas && this.cardUniforms.uAtlas.value?.source !== atlas.source) {
      const tex = atlas.clone();
      tex.anisotropy = 1;
      tex.needsUpdate = true;
      this.cardUniforms.uAtlas.value = tex;
    }
    t.visible = !!this.cardUniforms.uAtlas.value; // no atlas yet: no puffs
    this.low.comp.visible = false; // until drawPuffs has drawn them this frame
    t.material.uniforms.uOpacity.value = t.material.opacity;
    this.group.updateMatrixWorld();
    camera.getWorldPosition(_eye);
    camera.getWorldDirection(_fwd).add(_eye);
    this.group.worldToLocal(_eye);
    this.group.worldToLocal(_fwd).sub(_eye);
    this._eyeLocal = _eye;
    t.userData.sort(_eye);
    this.halfOn = t.userData.nearest < this.cellR * HALF_NEAR[this.halfOn ? 1 : 0];
    const at = THREE.MathUtils.clamp(t.userData.nearest * 0.9, 1, 1500);
    t.geometry.boundingSphere.center.copy(_eye).addScaledVector(_fwd, at);
  }

  // The first half of the half-size draw (HALF_NEAR), run by main.js after
  // everything has moved and just before the frame draws — never from inside
  // the frame: switching targets mid-pass makes a tiled phone GPU store and
  // reload the whole frame, and even the RTX paid over a millisecond for it. `linear` is a frame drawn
  // into a target of its own (gfx2's), which wants linear light, where the
  // canvas wants sRGB. Run or not, the front draws: skipped, the cards draw
  // in the frame at full size as they always did.
  drawPuffs(renderer, camera, linear) {
    const L = this.low, t = this.towers;
    if (!L || !this.halfRes || !this.halfOn || !this.group.visible || !t.visible) return;
    renderer.getDrawingBufferSize(_size);
    const w = Math.max(1, Math.ceil(_size.x / 2)), h = Math.max(1, Math.ceil(_size.y / 2));
    if (!L.rt || L.linear !== linear) {
      L.rt?.dispose();
      L.rt = new THREE.WebGLMultipleRenderTargets(w, h, 2, { depthBuffer: false });
      // Colour in the canvas's own bytes (sRGB, as the cards would have
      // blended on it), or half floats for gfx2's linear frame; depth needs
      // the floats either way.
      L.rt.texture[0].type = linear ? THREE.HalfFloatType : THREE.UnsignedByteType;
      L.rt.texture[1].type = THREE.HalfFloatType;
      L.linear = linear;
      if (linear) delete L.mat.defines.CARD_SRGB_OUT;
      else L.mat.defines.CARD_SRGB_OUT = "";
      L.mat.needsUpdate = true;
      const u = L.comp.material.uniforms;
      u.tColor.value = L.rt.texture[0];
      u.tDepth.value = L.rt.texture[1];
    } else L.rt.setSize(w, h);
    L.proxy.matrix.copy(this.group.matrixWorld); // its scene sits at the origin
    L.scene.fog = this.scene.fog;
    L.comp.material.uniforms.uProj.value.copy(camera.projectionMatrix);
    const prev = renderer.getRenderTarget();
    renderer.getClearColor(_clear);
    const clearA = renderer.getClearAlpha(), autoClear = renderer.autoClear;
    renderer.autoClear = true;
    renderer.setClearColor(0x000000, 0);
    renderer.setRenderTarget(L.rt);
    renderer.render(L.scene, camera);
    renderer.setRenderTarget(prev);
    renderer.setClearColor(_clear, clearA);
    renderer.autoClear = autoClear;
    t.visible = false;
    L.comp.visible = true;
  }

  // Deep in the weather, shed the far puffs (THIN_MAX): a card more than a
  // cell radius from the eye whose rank falls under the shed share thins to
  // nothing, and a card at nothing is parked off-screen by the shader, costing no
  // fill. Ranks run the golden ratio over the deck, so every cell and the
  // squall line shed evenly. Out of the weather, every card stands.
  thinCards(insideK) {
    const t = this.towers;
    const s = THREE.MathUtils.smoothstep(insideK, THIN_K[0], THIN_K[1]) * THIN_MAX;
    if (s <= 0 && !this._thinned) return;
    this._thinned = s > 0;
    const { cards, fade } = t.userData;
    const e = this._eyeLocal, r0 = this.cellR * 0.75, r1 = this.cellR;
    let moved = 0;
    for (let i = 0; i < cards.length; i++) {
      let f = 1;
      if (s > 0) {
        const rk = Math.min(1, Math.max(0, (s - (i * 0.618034) % 1) / THIN_RAMP));
        if (rk > 0) {
          puffMid(cards[i], _mid);
          const d = Math.hypot(_mid[0] - e.x, _mid[1] - e.y, _mid[2] - e.z);
          f = 1 - rk * THREE.MathUtils.smoothstep(d, r0, r1);
        }
      }
      moved = Math.max(moved, Math.abs(f - fade[i]));
      fade[i] = f;
    }
    if (moved > 0) t.userData.pushFade();
  }

  // The front swallows the fair-weather cumulus in its path: a sunlit puff
  // hanging in front of the anvil line read as a second sky pasted over the
  // storm. Every deck card inside the front's footprint (the squall line's
  // length by the cells' depth, in the marching frame) thins to nothing,
  // easing back over most of a cell radius outside it. `pose` null lets
  // them all back (the round cleared).
  swallowDecks(pose) {
    if (!this.cloudDecks || (!pose && !this._swallowed)) return; // set() runs every snapshot
    this._swallowed = !!pose;
    const r = this.cellR;
    const sinA = pose ? Math.sin(pose.ang) : 0, cosA = pose ? Math.cos(pose.ang) : 1;
    for (const deck of [this.cloudDecks.low, this.cloudDecks.high]) {
      const { cards, fade, pushFade } = deck?.userData ?? {};
      if (!fade) continue;
      for (let i = 0; i < cards.length; i++) {
        if (!pose) { fade[i] = 1; continue; }
        const dx = cards[i].x - pose.x, dz = cards[i].z - pose.z;
        const ox = dx * cosA - dz * sinA, oz = dx * sinA + dz * cosA;
        const ex = Math.max(0, Math.abs(ox) - this.halfLine);
        const ez = Math.max(0, Math.abs(oz) - r * 1.4);
        fade[i] = THREE.MathUtils.smoothstep(Math.hypot(ex, ez), 0, r * 0.8);
      }
      pushFade();
    }
  }

  // Lay a stair-stepped bolt from cell i's belly to the terrain below —
  // axis-aligned boxes only: hard vertical drops and right-angle jogs, so
  // the sky's own violence speaks the same pixel language as the terrain it
  // hits. World anchored (the front drifts ~5 m during a strike's life; the
  // scorch point staying put is what sells "it hit the ground").
  buildStrike(i, slot) {
    const [wx, wz] = this.cellWorld[i];
    const cs = (this.seed + slot * 2711 + i * 389) >>> 0;
    // Bolts bias toward the LEADING edge of the march (+Z in the front's
    // frame): half-buried flashes inside the wall are mood, but the readable
    // crack-to-the-ground moments belong on the side the storm is walking
    // toward — the side everyone under it is watching.
    const ry = this.group.rotation.y;
    const lx = (hash2(0, 91, cs) - 0.5) * this.cellR * 1.2;
    const lz = this.cellR * (0.3 + 0.6 * hash2(0, 92, cs));
    const bx = wx + lx * Math.cos(ry) + lz * Math.sin(ry);
    const bz = wz - lx * Math.sin(ry) + lz * Math.cos(ry);
    const groundY = Math.max(this.field.surfaceAt(bx, bz), this.field.waterLevel ?? 0);
    const segs = [];
    const TH = 5;
    let x = bx, z = bz, y = this.baseY + 150;
    // The channel walks down in many small, uneven moves: mostly short
    // vertical steps, the odd long straight plunge, and every few steps a
    // SLANTED run (a leaned box) so the profile isn't all right angles —
    // jagged the way a pixel-art bolt is jagged, not a staircase diagram.
    for (let k = 0; y > groundY + 4 && segs.length < this.C2G_SEGS - 8; k++) {
      const r1 = hash2(k, 95, cs), r2 = hash2(k, 96, cs);
      const r3 = hash2(k, 97, cs), r4 = hash2(k, 88, cs);
      const drop = Math.min(y - groundY, r1 < 0.2 ? 90 + r2 * 130 : 16 + r2 * 55);
      if (r3 < 0.4 && drop > 24) {
        // Slanted run: lean 15°-50° off vertical, in X or Z.
        const lean = (0.26 + 0.62 * hash2(k, 87, cs)) * (r4 < 0.5 ? 1 : -1);
        const L = drop / Math.cos(lean);
        if (hash2(k, 86, cs) < 0.5) {
          const dx = -Math.sin(lean) * L;
          segs.push([x + dx / 2, y - drop / 2, z, TH, L + TH, TH, 0, lean]);
          x += dx;
        } else {
          const dz = Math.sin(lean) * L;
          segs.push([x, y - drop / 2, z + dz / 2, TH, L + TH, TH, lean, 0]);
          z += dz;
        }
        y -= drop;
      } else {
        segs.push([x, y - drop / 2, z, TH, drop + TH, TH]);
        y -= drop;
      }
      if (y <= groundY + 4) break;
      // Sometimes two drops run straight through; otherwise a hard
      // right-angle jog — narrow shrugs and wide throws both — with a pull
      // back toward the channel so the bolt wanders without walking away.
      if (hash2(k, 85, cs) < 0.25) continue;
      let len = (hash2(k, 84, cs) - 0.5) * 2 * (hash2(k, 83, cs) < 0.5 ? 34 : 130);
      if (r3 >= 0.5) {
        len -= (x - bx) * 0.5;
        segs.push([x + len / 2, y, z, Math.abs(len) + TH, TH, TH]);
        x += len;
      } else {
        len -= (z - bz) * 0.5;
        segs.push([x, y, z + len / 2, TH, TH, Math.abs(len) + TH]);
        z += len;
      }
    }
    // One forked branch off the upper channel, dying out mid-air — the
    // detail every pixel-lightning drawing agrees on.
    let fx = bx, fz = bz;
    let fy = this.baseY + 150 - (60 + hash2(1, 98, cs) * 120);
    const fTH = 3.2;
    const fSign = hash2(0, 93, cs) < 0.5 ? 1 : -1;
    for (let f = 0; f < 4 && segs.length < this.C2G_SEGS; f++) {
      if (f % 2 === 0) {
        const len = (18 + hash2(f, 99, cs) * 70) * fSign;
        segs.push([fx + len / 2, fy, fz, Math.abs(len) + fTH, fTH, fTH]);
        fx += len;
      } else {
        const drop = 40 + hash2(f, 94, cs) * 80;
        segs.push([fx, fy - drop / 2, fz, fTH, drop + fTH, fTH]);
        fy -= drop;
      }
    }
    this.strikes[i] = { segs, hit: new THREE.Vector3(x, groundY, z), bright: 0 };
  }

  // A cloud-to-cloud stroke: a stair-stepped arc crawling the bellies from
  // cell j toward cell j+1 — long horizontal bars with vertical kicks,
  // glimpsed through the cloud mass it lights up.
  buildArc(j, slot) {
    const [ax, az] = this.cellWorld[j];
    const [bx, bz] = this.cellWorld[j + 1];
    const cs = (this.seed + slot * 3499 + j * 577) >>> 0;
    const band = this.topY - this.baseY;
    let y = this.baseY + band * (0.22 + 0.25 * hash2(0, 81, cs));
    let x = ax + (hash2(0, 82, cs) - 0.5) * this.cellR * 0.5;
    let z = az + (hash2(0, 83, cs) - 0.5) * this.cellR * 0.5;
    const tx = bx + (hash2(0, 84, cs) - 0.5) * this.cellR * 0.5;
    const tz = bz + (hash2(0, 85, cs) - 0.5) * this.cellR * 0.5;
    const segs = [];
    const TH = 5.0; // thick enough to read through a kilometre of cloud
    const STEPS = 5;
    for (let sIdx = 0; sIdx < STEPS && segs.length < this.C2C_SEGS - 2; sIdx++) {
      const legX = (tx - x) / (STEPS - sIdx);
      const legZ = (tz - z) / (STEPS - sIdx);
      segs.push([x + legX / 2, y, z, Math.abs(legX) + TH, TH, TH]);
      x += legX;
      segs.push([x, y, z + legZ / 2, TH, TH, Math.abs(legZ) + TH]);
      z += legZ;
      const kick = (hash2(sIdx, 86, cs) - 0.5) * band * 0.3;
      segs.push([x, y + kick / 2, z, TH, Math.abs(kick) + TH, TH]);
      y += kick;
    }
    this.arcs[j] = {
      segs,
      mid: new THREE.Vector3((ax + bx) / 2, y, (az + bz) / 2),
      bright: 0,
    };
  }

  drawStrikes() {
    let n = 0;
    let anyBright = 0;
    const put = (st) => {
      if (!st || st.bright <= 0.01) return;
      anyBright = Math.max(anyBright, st.bright);
      for (const [x, y, z, sx, sy, sz, rx, rz] of st.segs) {
        if (n >= this.BOLT_CAP) return;
        this.dummy.position.set(x, y, z);
        this.dummy.scale.set(sx, sy, sz);
        this.dummy.rotation.set(rx ?? 0, 0, rz ?? 0);
        this.dummy.updateMatrix();
        this.bolt.setMatrixAt(n++, this.dummy.matrix);
      }
    };
    for (const st of this.strikes) put(st);
    for (const arc of this.arcs) put(arc);
    uploadInstances(this.bolt, n);
    this.bolt.material.opacity = anyBright;
  }
}

// The rain curtain's alpha: across each face a soft hump (clear at both side
// edges), down it full under the cloud base and thinning to a third at the
// ground. Read from the green channel, as three's alphaMap is.
function rainAlphaMap() {
  const W = 32, H = 32;
  const px = new Uint8Array(W * H * 4);
  for (let j = 0; j < H; j++) {
    const v = j / (H - 1); // 0 at the box's floor, 1 at its top (the cloud base)
    const down = 0.35 + 0.65 * THREE.MathUtils.smoothstep(v, 0, 0.7);
    for (let i = 0; i < W; i++) {
      const u = i / (W - 1);
      const across = Math.sin(Math.PI * u) ** 1.5;
      const a = Math.round(255 * across * down);
      px.set([a, a, a, 255], (j * W + i) * 4);
    }
  }
  const tex = new THREE.DataTexture(px, W, H);
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

// The front's puffs at half the frame's size (HALF_NEAR): drawn into their own
// pair of targets before the frame, then laid over it in one screen-sized
// card at the front's place among the see-through draws. The second target
// carries each pixel's depth into the cloud, weighted the way the puffs hide
// one another (blended "over", like the colour), so the lay can stand at the
// depth where that pixel's cloud mostly is: a hill or a plane nearer than it
// stays in front of the storm, one behind it is lost in the weather.
function lowResPuffs(towers) {
  const tm = towers.material;
  // The cards' own shader, writing a second colour: (depth, -, -, alpha).
  const vert = tm.vertexShader
    .replace("varying float vAlpha;", "varying float vAlpha;\nvarying float vDepth;")
    .replace("gl_Position = projectionMatrix * mvPosition;", "gl_Position = projectionMatrix * mvPosition;\n  vDepth = -mvPosition.z;");
  const frag = "layout(location = 0) out highp vec4 pc_fragColor;\nlayout(location = 1) out highp vec4 pc_depth;\n"
    + "#define gl_FragColor pc_fragColor\nvarying float vDepth;\n"
    + tm.fragmentShader.replace(/\}\s*$/, "  pc_depth = vec4(vDepth, 0.0, 0.0, gl_FragColor.a);\n}\n");
  if (vert === tm.vertexShader || !frag.includes("pc_depth = ")) throw new Error("storm: card shader changed under lowResPuffs");
  const mat = new THREE.ShaderMaterial({
    uniforms: tm.uniforms, defines: { ...tm.defines }, vertexShader: vert, fragmentShader: frag,
    glslVersion: THREE.GLSL3, transparent: true, depthWrite: false, depthTest: false, fog: true,
  });
  const proxy = new THREE.Mesh(towers.geometry, mat);
  proxy.frustumCulled = false;
  proxy.matrixAutoUpdate = false; // drawPuffs hands it the front's pose
  const scene = new THREE.Scene();
  scene.add(proxy);

  const comp = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.ShaderMaterial({
    uniforms: { tColor: { value: null }, tDepth: { value: null }, uProj: { value: new THREE.Matrix4() } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`,
    fragmentShader: /* glsl */ `
      uniform sampler2D tColor;
      uniform sampler2D tDepth;
      uniform mat4 uProj;
      varying vec2 vUv;
      void main() {
        vec4 c = texture2D(tColor, vUv);
        if (c.a < 0.004) discard;
        vec4 d = texture2D(tDepth, vUv);
        vec4 clip = uProj * vec4(0.0, 0.0, -d.r / max(d.a, 1e-4), 1.0);
        gl_FragDepth = clamp(0.5 + 0.5 * clip.z / clip.w, 0.0, 1.0);
        gl_FragColor = c; // premultiplied: the puffs' colour, already blended
      }`,
    transparent: true, depthTest: true, depthWrite: false,
    blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor,
    blendSrcAlpha: THREE.OneFactor, blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
  }));
  // Sorts where the cards would: the same sphere, moved by sortCards.
  comp.geometry.boundingSphere = towers.geometry.boundingSphere;
  comp.frustumCulled = false;
  comp.visible = false;
  return { comp, proxy, scene, mat, rt: null, linear: null };
}

const _eye = new THREE.Vector3();
const _size = new THREE.Vector2();
const _clear = new THREE.Color();
const _fwd = new THREE.Vector3();
const _up12 = new THREE.Vector3(0, 14, 0);
const _mid = [0, 0, 0];
